/**
 * HelpService
 * In-app help and onboarding: page help, tab help, guided tours, per-person tour progress and help
 * settings per app, "Ask AI about this page" and the admin help content editor.
 * Route: api/help (CCMS.Api.System)
 *
 * Reading help and asking needs a signed-in user; the admin routes need the Admin role or the
 * Help.Content.Manage right. Ask is limited to 20 questions per person per hour (429).
 */
import { BaseService } from '../base/BaseService';
const enc = (value) => encodeURIComponent(value);
function isNotFound(err) {
    const e = err;
    return e?.status === 404 || e?.code === 'NOT_FOUND';
}
/** A 2xx with an empty or non-envelope body (e.g. a bare 200 on delete) still succeeded. */
function isEmptySuccess(err) {
    const e = err;
    return e?.code === 'INVALID_RESPONSE' && typeof e.status === 'number' && e.status >= 200 && e.status < 300;
}
export class HelpService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? HelpService.DEFAULT_SERVICE_PATH,
            basePath: HelpService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ------------------------------------------------------------------ reading help
    /** A page's help (summary, tabs, tours). Null when no help has been written yet (404). GET api/help/pages/{pageKey} */
    async getHelpPage(pageKey) {
        try {
            const res = await this.http.get(this.buildUrl(`pages/${enc(pageKey)}`));
            return res?.data ?? null;
        }
        catch (err) {
            if (isNotFound(err))
                return null;
            throw err;
        }
    }
    /** Every active help page of an app. GET api/help/apps/{appKey} */
    async getHelpAppPages(appKey) {
        const res = await this.http.get(this.buildUrl(`apps/${enc(appKey)}`));
        return res?.data ?? [];
    }
    // ------------------------------------------------------------------ my tour progress
    /** The signed-in person's tour progress. GET api/help/me/progress */
    async getMyHelpProgress() {
        const res = await this.http.get(this.buildUrl('me/progress'));
        return res?.data ?? [];
    }
    /** Records a tour as completed or dismissed for its version. PUT api/help/me/progress/{tourKey} */
    async setMyHelpProgress(tourKey, request) {
        const res = await this.http.put(this.buildUrl(`me/progress/${enc(tourKey)}`), request);
        if (!res?.data)
            throw new Error(res?.message || `Failed to save progress for tour ${tourKey}`);
        return res.data;
    }
    /** Forgets a tour's progress so it shows again. DELETE api/help/me/progress/{tourKey} */
    async resetMyHelpProgress(tourKey) {
        try {
            await this.http.delete(this.buildUrl(`me/progress/${enc(tourKey)}`));
            return true;
        }
        catch (err) {
            // Nothing stored for this tour is the same as reset.
            if (isNotFound(err) || isEmptySuccess(err))
                return true;
            throw err;
        }
    }
    // ------------------------------------------------------------------ my help settings per app
    /**
     * The signed-in person's help settings per app. Only apps with a saved setting are listed; any other app
     * uses the defaults (tours open by themselves). GET api/help/me/preferences
     */
    async getMyHelpPreferences() {
        const res = await this.http.get(this.buildUrl('me/preferences'));
        return res?.data ?? [];
    }
    /**
     * Saves whether an app's tours may open by themselves for the signed-in person.
     * 404 when no help page uses the app key. PUT api/help/me/preferences/{appKey}
     */
    async setMyHelpAppPreference(appKey, request) {
        const res = await this.http.put(this.buildUrl(`me/preferences/${enc(appKey)}`), request);
        if (!res?.data)
            throw new Error(res?.message || `Failed to save the help settings for ${appKey}`);
        return res.data;
    }
    // ------------------------------------------------------------------ Ask AI
    /**
     * Ask AI about the page the person is on. Always send pageKey, tabKey and currentRoute.
     * Throws a CCMSError with status 429 when the hourly limit is reached.
     * POST api/help/ask
     */
    async askHelp(request) {
        const res = await this.http.post(this.buildUrl('ask'), request);
        const data = res?.data;
        if (!data)
            throw new Error(res?.message || 'AI help did not answer');
        return { ...data, links: data.links ?? [], followUps: data.followUps ?? [] };
    }
    /** Whether Ask AI is switched on (only `enabled` and `message` are filled). GET api/help/ask/status */
    async getHelpAskStatus() {
        const res = await this.http.get(this.buildUrl('ask/status'));
        const data = res?.data;
        return { enabled: !!data?.enabled, message: data?.message ?? null, html: null, links: [], followUps: [] };
    }
    // ------------------------------------------------------------------ admin (Admin or Help.Content.Manage)
    /** Every help page incl. inactive ones, optionally for one app. GET api/help/admin/pages?appKey= */
    async getHelpAdminPages(appKey) {
        const res = await this.http.get(this.buildUrlWithParams('admin/pages', { appKey: appKey || undefined }));
        return res?.data ?? [];
    }
    /** Creates or updates a page's help; tabs replace the page's tabs. PUT api/help/admin/pages/{pageKey} */
    async saveHelpPage(pageKey, request) {
        const res = await this.http.put(this.buildUrl(`admin/pages/${enc(pageKey)}`), request);
        if (!res?.data)
            throw new Error(res?.message || `Failed to save help for ${pageKey}`);
        return res.data;
    }
    /** Soft-deletes a page's help. DELETE api/help/admin/pages/{pageKey} */
    async deleteHelpPage(pageKey) {
        try {
            await this.http.delete(this.buildUrl(`admin/pages/${enc(pageKey)}`));
        }
        catch (err) {
            if (!isEmptySuccess(err))
                throw err;
        }
        return true;
    }
    /** Creates or updates a tour; steps replace the tour's steps. PUT api/help/admin/tours/{tourKey} */
    async saveHelpTour(tourKey, request) {
        const res = await this.http.put(this.buildUrl(`admin/tours/${enc(tourKey)}`), request);
        if (!res?.data)
            throw new Error(res?.message || `Failed to save tour ${tourKey}`);
        return res.data;
    }
    /** Soft-deletes a tour. DELETE api/help/admin/tours/{tourKey} */
    async deleteHelpTour(tourKey) {
        try {
            await this.http.delete(this.buildUrl(`admin/tours/${enc(tourKey)}`));
        }
        catch (err) {
            if (!isEmptySuccess(err))
                throw err;
        }
        return true;
    }
}
HelpService.DEFAULT_SERVICE_PATH = 'system';
HelpService.BASE_PATH = 'api/help';
//# sourceMappingURL=HelpService.js.map